import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  FullscreenControl,
  GeolocateControl,
  Map,
} from 'react-map-gl/maplibre';
import * as maplibre from 'maplibre-gl';
import isEqual from 'lodash/isEqual';
import {
  geometryPositions,
  normalizeAreas,
  validPosition,
} from './searchAreaGeometry';

const SQUAREFOOT_HQ = { latitude: 40.712784, longitude: -74.005941 };
const VIEWPORT_OFFSET = 65;
const VIEWPORT_PADDING = {
  top: VIEWPORT_OFFSET,
  right: VIEWPORT_OFFSET,
  bottom: VIEWPORT_OFFSET,
  left: VIEWPORT_OFFSET,
};

function BaseMap({
  children,
  autoCenter,
  initialViewState,
  className,
  style,
  ...restProps
}) {
  const ref = useRef(null);
  const lastBounds = useRef(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    requestAnimationFrame(() => {
      const map = ref.current && ref.current.getMap();
      if (!map || !autoCenter || !loaded) return;

      const bounds = new maplibre.LngLatBounds();

      React.Children.forEach(children, (child) => {
        const props = child && (child.props || child.attributes);
        if (!props) return;
        const building = props.listing && props.listing.building;
        const position = building
          ? [building.lng, building.lat]
          : [props.longitude, props.latitude];
        if (validPosition(position)) bounds.extend(position);
        if (props.data)
          normalizeAreas(props.data).features.forEach((feature) => {
            geometryPositions(feature.geometry).forEach((point) =>
              bounds.extend(point)
            );
          });
      });

      if (
        !bounds.isEmpty() &&
        !isEqual(
          bounds.toArray(),
          lastBounds.current && lastBounds.current.toArray()
        )
      ) {
        lastBounds.current = bounds;
        map.fitBounds(bounds, {
          speed: 3,
          maxZoom: 16,
          linear: true,
        });
      }
    });
  }, [children, autoCenter, loaded]);

  return (
    <div className={className} style={style}>
      <Map
        ref={ref}
        maxZoom={18}
        {...restProps}
        initialViewState={{
          ...SQUAREFOOT_HQ,
          zoom: 16,
          padding: VIEWPORT_PADDING,
          ...initialViewState,
        }}
        onLoad={(event) => {
          setLoaded(true);
          if (restProps.onLoad) restProps.onLoad(event);
        }}
        attributionControl={false}
        mapStyle="https://tiles.openfreemap.org/styles/bright"
      >
        <FullscreenControl position="top-right" />
        <GeolocateControl />
        {children}
      </Map>
    </div>
  );
}

BaseMap.propTypes = {
  children: PropTypes.node,
  autoCenter: PropTypes.bool,
  initialViewState: PropTypes.object,
  className: PropTypes.string,
  style: PropTypes.object,
};

BaseMap.defaultProps = {
  children: null,
  autoCenter: true,
  initialViewState: null,
  className: null,
  style: null,
};

export const LinePaintOptions = {
  'line-color': '#004f8a',
  'line-width': 2,
};

export const FillPaintOptions = {
  'fill-color': '#004f8a',
  'fill-opacity': 0.4,
};

export * from 'react-map-gl/maplibre';
export default BaseMap;
